import React, { useState } from 'react';
import { Scissors, ZoomIn, X } from 'lucide-react';
import { galleryImages } from '../data/mock';

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('הכל');

  const categories = ['הכל', 'פייד', 'קלאסי', 'טקסטורה', 'שיער ארוך', 'המספרה'];
  
  const filteredImages = activeFilter === 'הכל' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeFilter);

  return (
    <section id="gallery" className="py-20 sm:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(45deg, #c9a962 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" dir="rtl">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-px w-12 bg-[#c9a962]" />
            <Scissors className="w-5 h-5 text-[#c9a962]" />
            <div className="h-px w-12 bg-[#c9a962]" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            גלריית <span className="text-[#c9a962]">העבודות</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            הצצה לתספורות והמספרה היוקרתית שלנו
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-start md:justify-center gap-3 mb-10 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((category) => (
            <button
              key={category}
              data-testid={`gallery-filter-${category}`}
              onClick={() => setActiveFilter(category)}
              className={`px-5 py-2 text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                activeFilter === category
                  ? 'bg-[#c9a962] text-slate-950'
                  : 'bg-slate-900 text-slate-400 hover:text-[#c9a962] border border-slate-800 hover:border-[#c9a962]/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image, index) => (
            <div
              key={image.id}
              className={`relative group cursor-pointer overflow-hidden ${
                index === 0 && activeFilter === 'הכל' ? 'md:col-span-2 md:row-span-2' : ''
              }`}
              onClick={() => setSelectedImage(image)}
            >
              <div className={`relative ${
                index === 0 && activeFilter === 'הכל' ? 'h-full min-h-[300px] md:min-h-[500px]' : 'h-48 sm:h-64 md:h-72'
              }`}>
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                
                {/* Hover Effect */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-14 h-14 bg-[#c9a962] flex items-center justify-center">
                    <ZoomIn className="w-6 h-6 text-slate-950" />
                  </div>
                </div>
                
                {/* Label */}
                <div className="absolute bottom-4 right-4 left-4">
                  <span className="text-white text-sm bg-slate-950/80 px-3 py-1 inline-block">
                    {image.category}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-[#c9a962] transition-colors z-10"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="max-w-4xl max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-950 to-transparent p-6">
              <p className="text-white font-semibold">{selectedImage.alt}</p>
              <p className="text-[#c9a962] text-sm">{selectedImage.category}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
